import { useEffect, useState, useRef, useCallback } from "react";
import {
  fetchZonesPOReports,
  fetchZonePartiesPOReports,
  fetchDashboardReports
} from "Service/ApiService";

/* ======================= HELPERS ======================= */
const useDebounce = (value, delay = 600) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

const ReportsDashboardController = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isFromApp = localStorage.getItem("isFromApp") === "true";

  const [zoneOptions, setZoneOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);

  const [loadingZones, setLoadingZones] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(true);

  const [filters, setFilters] = useState({
    zone: [],
    customer: []
  });

  const [initialFiltersSet, setInitialFiltersSet] = useState(false);
  const debouncedFilters = useDebounce(filters, 600);
  const firstDashboardCallRef = useRef(false);
  const lastRequestedFiltersRef = useRef(null);
  const dashboardRequestRunningRef = useRef(false);
  const dashboardRequestQueuedRef = useRef(false);
  const filtersRef = useRef(filters);

  const cloading = loadingZones || loadingCustomers || loadingDashboard;

  const mapZones = (zones = []) =>
    zones.map((z, idx) => ({
      label: z.region?.trim() || "(NO REGION)",
      value: `${z.zoneCode}__${idx}`,
      zoneCode: z.zoneCode,
      amount: z.amount
    }));

  const mapCustomers = (parties = []) =>
    parties.map((p) => ({
      label: `${p.partyName} -  ${p.city} - ${p.partyCode?.trim()}`,
      value: p.partyCode,
      amount: p.amount
    }));

  const getZoneCodes = (zones = []) => zones.map((z) => z.zoneCode).join(",");

  const getFiltersSignature = (f) =>
    `${(f?.zone ?? []).map((z) => z.zoneCode).join(",")}|${(f?.customer ?? []).map((c) => c.value).join(",")}`;

  const updateFilters = useCallback((updater) => {
    setFilters((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      filtersRef.current = next;
      return next;
    });
  }, []);

  const fetchCustomers = useCallback(
    async ({ selectedZones = [], search = "", preserveSelectedCustomers = false } = {}) => {
      const zoneCodes = getZoneCodes(selectedZones);

      try {
        setLoadingCustomers(true);

        const res = await fetchZonePartiesPOReports(user.userCode, {
          search,
          type: "collection",
          zone: zoneCodes
        });

        const mappedCustomers = res?.status && Array.isArray(res.parties) ? mapCustomers(res.parties) : [];
        setCustomerOptions(mappedCustomers);

        updateFilters((prev) => {
          let nextCustomers = mappedCustomers;

          if (preserveSelectedCustomers && prev.customer.length) {
            const retained = prev.customer.filter((selected) =>
              mappedCustomers.some((option) => option.value === selected.value)
            );
            nextCustomers = retained.length ? retained : mappedCustomers;
          }

          return {
            ...prev,
            customer: nextCustomers
          };
        });

        setInitialFiltersSet(true);
        return mappedCustomers;
      } catch (err) {
        console.error("Dashboard customer fetch error:", err);
        setCustomerOptions([]);
        updateFilters((prev) => ({
          ...prev,
          customer: []
        }));
        setInitialFiltersSet(true);
        return [];
      } finally {
        setLoadingCustomers(false);
      }
    },
    [updateFilters, user.userCode]
  );

  const loadAllZones = useCallback(async () => {
    try {
      setLoadingZones(true);
      setLoadingCustomers(true);
      setInitialFiltersSet(false);

      const res = await fetchZonesPOReports(user.userCode, "collection");
      const mappedZones = res?.status && Array.isArray(res.zones) ? mapZones(res.zones) : [];

      setZoneOptions(mappedZones);
      updateFilters({
        zone: mappedZones,
        customer: []
      });
      await fetchCustomers({ selectedZones: mappedZones });
    } catch (err) {
      console.error("Dashboard zone fetch error:", err);
      setZoneOptions([]);
      updateFilters({
        zone: [],
        customer: []
      });
      await fetchCustomers({ selectedZones: [] });
    } finally {
      setLoadingZones(false);
    }
  }, [fetchCustomers, updateFilters, user.userCode]);

  useEffect(() => {
    loadAllZones();
    localStorage.setItem("SelectedLedgerCustomer", null);
    localStorage.setItem("SelectedDivLedgerCustomer", null);
  }, [loadAllZones]);

  const handleFilterChange = (field) => (value) => {
    const nextValue = value || [];

    if (field === "zone") {
      updateFilters((prev) => ({
        ...prev,
        zone: nextValue,
        customer: []
      }));
      void fetchCustomers({
        selectedZones: nextValue,
        preserveSelectedCustomers: false
      });
      return;
    }

    updateFilters((prev) => ({
      ...prev,
      [field]: nextValue
    }));
  };

  const callDashboardAPI = useCallback(async () => {
    if (dashboardRequestRunningRef.current) {
      dashboardRequestQueuedRef.current = true;
      return;
    }

    dashboardRequestRunningRef.current = true;
    setLoadingDashboard(true);

    try {
      while (true) {
        dashboardRequestQueuedRef.current = false;
        const f = filtersRef.current;

        try {
          const zoneCodes = (f?.zone ?? []).map((z) => z.zoneCode).join(",");
          const zoneNames = (f?.zone ?? []).map((z) => z.label).join(",");
          const partyCodes = (f?.customer ?? []).map((c) => c.value).join(",");

          const res = await fetchDashboardReports(user.userCode, 1, zoneCodes, partyCodes, "", "", zoneNames);
          setDashboardData(res?.status ? res : null);
        } catch (err) {
          console.error("Dashboard API error:", err);
          setDashboardData(null);
        }

        if (!dashboardRequestQueuedRef.current) break;
      }
    } finally {
      dashboardRequestRunningRef.current = false;
      setLoadingDashboard(false);
    }
  }, [user.userCode]);

  useEffect(() => {
    if (loadingZones || loadingCustomers || !initialFiltersSet) return;

    if (!firstDashboardCallRef.current) {
      firstDashboardCallRef.current = true;
      lastRequestedFiltersRef.current = getFiltersSignature(filtersRef.current);
      callDashboardAPI();
      return;
    }

    const nextSig = getFiltersSignature(debouncedFilters);
    if (lastRequestedFiltersRef.current === nextSig) return;

    lastRequestedFiltersRef.current = nextSig;
    filtersRef.current = debouncedFilters;
    callDashboardAPI();
  }, [loadingZones, loadingCustomers, initialFiltersSet, debouncedFilters, callDashboardAPI]);

  const handleCustomerSearch = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) return;

    await fetchCustomers({
      selectedZones: filtersRef.current.zone,
      search: inputValue,
      preserveSelectedCustomers: true
    });
  };

  const loadAllCustomers = useCallback(async () => {
    await fetchCustomers({
      selectedZones: filtersRef.current.zone,
      preserveSelectedCustomers: false
    });
  }, [fetchCustomers]);

  return {
    cloading,
    loadingDashboard,
    zoneOptions,
    customerOptions,
    filters,
    handleFilterChange,
    handleCustomerSearch,
    loadAllCustomers,
    loadAllZones,
    dashboardData,
    isFromApp
  };
};

export default ReportsDashboardController;
